import './App.scss';

import { Amplify } from 'aws-amplify';
import { LoadingOverlay, Snackbar, SubNavigation } from '@finpay-development/shared-components';
import { CircularProgress, Grid, Box, Button } from '@mui/material';
import jwt from 'jwt-decode';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';

import {
  setIsPublicMode,
  setPathName,
  setUrlParameters,
} from './admin/state/users/admin-slice';
import { setIsChannel, setChannelType } from './guest/state/guest-slice';
import { PublicPagesWrapper } from './guest/components/PublicPagesWrapper';
import { isCurrentUrlPublic } from './guest/misc/helpers';
import RouteConfiguration, { RouteConfigProps } from './route-configuration';
import AuthenticationMonitor from './security/components/authentication-monitor';
import GuardedComponent from './security/components/guarded-component';
import SessionTimeout from './security/components/session-timeout';
import { hideStatus } from './security/state/user-slice';
import { checkIfUserIsAuthenticated, logUserOut, refreshToken } from './security/state/user-thunk';
import FppFooter from './shared/components/footer';
import FppHeader from './shared/components/header';
import { getConfiguration } from './shared/configuration/config-settings';
import { DecodedIdToken } from './shared/model/decodedIdToken';
import { RootState } from './shared/state/root-reducer';
import { AppDispatch } from './shared/state/store';
import FinDigitalPlatformFooter from './shared/components/findigital-platform-footer';
import { PaymentHubButton } from './payment-hub/components/payment-hub-button';
import {getMetaData} from './meta-data/state/meta-data-thunk';
import {clearFacilityPayers, clearVOB} from "./admissions-advisor/state/vob-slice";
import {clearPatient} from "./admissions-advisor/state/vob-patient-slice";
import {clearEstimate} from "./admissions-advisor/state/estimator-slice";
import {clearRiskAssessment} from "./admissions-advisor/state/risk-assessment-slice";
import {loadVobClassifications} from "./shared/state/vob-thunk";
import {configGetPayers} from "./admin-configuration/state/admin-configuration-thunk";
import {
  getClients
} from './implementation-specialist/state/clients/implementation-clients-thunk';
import { clearPatientVobList, clearSelectedVob } from './shared/state/vob-slice';
import { clearSavedEstimatesList, clearSelectedEstimate } from './shared/state/estimator-slice';
import {getClientWaivers, getRecentPatient} from './patient/state/patient-thunk';
import { getRateAsPayorMap } from './shared/state/estimator-thunk';
import {clearEncounter} from './patient/state/patient-slice';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  UnauthenticatedAuthRouter
} from './security/components/unauthenticated-auth-router';

// get the configuration based on current url dns name
const config = getConfiguration();

let cookieDomain;

switch(true){
  case window.location.hostname.includes('localhost'):
    cookieDomain = window.location.hostname.includes('patient') ? 'patient.localhost' : '.localhost'
    break;
  case window.location.hostname.includes('myfinpay'):
    cookieDomain = ".myfinpay.net"
    break;
  default:
    //Need to support config, report, and admin portals.
    //Prod env doesn't have the env in the domain name
    cookieDomain = config.app_environment==='prod' ? ".finpay.net" : `.${config.app_environment}.finpay.net`
}

// Setup Amplify AWS User Pool Configuration
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws_userPoolId,
      userPoolClientId: config.aws_userPoolWebClientId,
      identityPoolId: ""
    },
  },
});


cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
  domain: window.location.hostname.includes('localhost') ? ".localhost" :
      (config.app_environment==='prod' ? ".finpay.net" : `.${config.app_environment}.finpay.net`),
  secure: true,
  //expire in days
  expires: 1,
  sameSite: 'none'
}));


function App() {
  const dispatch = useDispatch<AppDispatch>();
  const subNavItemsToIgnore = ["Logs", "Configuration", "Roles", "Users", "Clients", "Forward Document", "Admissions Inbound"];
  const existingPatientLoginMessage_1 = "Great News!"
  const existingPatientLoginMessage_2 = "Based on the information you provided, we see you are already a FinPay patient with an active payment plan.";
  const existingPatientLoginMessage_3 = "Please login below to access your account in the FinPay Patient Portal";

  const isPublic = isCurrentUrlPublic(window.location.href);

  if (isPublic.isCurrentUrlPublic) {
    dispatch(setIsPublicMode(isPublic.isCurrentUrlPublic));
  }
  if (isPublic.channelStatus) {
    dispatch(setUrlParameters(isPublic.guestInfo));
    dispatch(setChannelType(isPublic.channelType));
    dispatch(setIsChannel(isPublic.channelStatus));
  }
  const [isMobile, setIsMobile] = useState(false)
  const hasCheckedUser = useRef(false);

  const handleResize = () => {
    if (window.innerWidth < 960) {
        setIsMobile(true)
    } else {
        setIsMobile(false)
    }
  }

  const stateFields = {
    userIsAuthenticated: useSelector((state: RootState) => state.userContext.userIsAuthenticated),
    userRolePages: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRolePages),
    subNav: useSelector((state: RootState) => state.userContext.userProfile.SubNav),
    isAccountHolderUser: useSelector((state: RootState) => state.userContext.userProfile.userRole.roleName === "Account Holder"),
    applicationStatus: useSelector((state: RootState) => state.userContext.applicationStatus),
    isLoading: useSelector((state: RootState) => state.userContext.isLoading),
    idToken: useSelector((state: RootState) => state.userContext.userProfile.tokenState.jwtIdToken),
    isPublicMode: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicMode),
    isPublicModeLoggedIn: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicModeLoggedIn),
    guestContext: useSelector((state: RootState) => state.guestContext),
    isSSO: useSelector((state: RootState) => state.userContext.isSSO),
    isSSOLoading: useSelector((state: RootState) => state.userContext.isSSOLoading),
    channelType: useSelector((state: RootState) => state.guestContext.channelType),
    isChannel: useSelector((state: RootState) => state.guestContext?.isChannel),
    userRoleId: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRoleId),
    showLogin: useSelector((state: RootState) => state.adminContext.adminUserContext.showLogin),
    pathName: useSelector((state: RootState) => state.adminContext.adminUserContext.pathName),
    allClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.allClients || []),
    isLoadingClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.isLoading || false),
    allowedClients: useSelector((state: RootState) => state.userContext.userProfile.allowedClients || []),
    userId: useSelector((state: RootState) => state.userContext.userProfile.userId),
  }

  const {
    userIsAuthenticated,
    userRolePages,
    subNav,
    isAccountHolderUser,
    applicationStatus,
    isLoading,
    idToken,
    isPublicMode,
    isPublicModeLoggedIn,
    guestContext,
    isSSO,
    isSSOLoading,
    channelType,
    isChannel,
    userRoleId,
    showLogin,
    pathName,
    allClients,
    isLoadingClients,
    userId,
    allowedClients,
  } = stateFields;

  // Show button if any client in the user's allowed client list has billingEngineVersion === 'V3'
  // allowedClients with clientId === 0 is wildcard (all clients)
  const shouldShowHubButton = useMemo(() => {
    if (isLoadingClients || !allowedClients?.length) {
      return false;
    }
    if (!allClients || allClients.length === 0) {
      return false;
    }
    const hasWildcard = allowedClients.some((c) => c.clientId === 0);
    if (hasWildcard) {
      return allClients.some((c) => c.billingEngineVersion === 'V3');
    }
    const allowedClientIds = allowedClients.map((c) => c.clientId);
    return allClients.some(
      (c) => allowedClientIds.includes(c.clientId) && c.billingEngineVersion === 'V3'
    );
  }, [allClients, isLoadingClients, allowedClients]);

  useEffect(() => {
    const path = window.location.pathname
    if(path.includes('forgot-password')){
      dispatch(logUserOut());
      dispatch(setPathName('forgot-password'))
    }else if(path.includes('webpayment')){
      dispatch(setPathName('webpayment'))
    }else if(path.includes('payment-program-enrollment')){
      dispatch(setPathName('payment-program-enrollment'))
    }
  }, [window.location.pathname]);

  const hasAccessToPage = (pageName: string) => {
    return (
      userRolePages.findIndex((page) => {
        return (
          page?.sysRolePage?.rolePageName === pageName &&
          page.isEnabled === true
        );
      }) !== -1
    );
  };

  useEffect(()=>{
    if(userIsAuthenticated && userRoleId !== 4){
      //fetching metadata in case browser is refreshed
      dispatch(getMetaData());
      dispatch(configGetPayers(-2));
      dispatch(getClients({
        includeFacility: true,
      }))
      dispatch(loadVobClassifications(-2));
      dispatch(getClientWaivers())
      dispatch(getRateAsPayorMap({paramId: -2}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dispatch, userIsAuthenticated])

  useEffect(() => {
    if (userIsAuthenticated && userId && userId > 0) {
      dispatch(getRecentPatient(userId));
    }
  }, [dispatch, userIsAuthenticated, userId]);

  // though the token is automatically refreshed when it expires and an api call happens,
  // this function may have some performance benefits due to caching.
  useEffect(() => {
    if (idToken) {
      const tokenInfo: DecodedIdToken = jwt(idToken);
      const timeoutMs = tokenInfo.exp * 1000 - new Date().getTime() + 500;
      setTimeout(() => {
        dispatch(refreshToken());
      }, timeoutMs);
    }
  }, [idToken, dispatch]);

  // Set user_id in Google Analytics from JWT token
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      const MEASUREMENT_ID = "G-N0S7JDQJJX"
      if (userIsAuthenticated && idToken) {
        try {
          // TODO: fix DecodedToken type
          
          const tokenInfo: any = jwt(idToken);
          const userId = `${tokenInfo.userId}-${tokenInfo.allowedClients}-${tokenInfo.email}`;
          // const userId = String(tokenInfo.userId);
          if (userId) {
            (window as any).gtag('set', {'user_id': userId});

            // Send (or re-send) page_view with user_id attached
            (window as any).gtag('config', MEASUREMENT_ID, {
              user_id: userId,
              page_path: window.location.pathname,
            });
          }
        } catch (error) {
          console.error('Error setting Google Analytics user_id:', error);
        }
      } else if (!userIsAuthenticated) {
        // Clear user_id when user logs out
        // (window as any).gtag('set', {'user_id': null});
        (window as any).gtag('config', MEASUREMENT_ID, {
          user_id: null,
          page_path: window.location.pathname,
        });
      }
    }
  }, [userIsAuthenticated, idToken]);

  useEffect(() => { handleResize() }, []);
  useEffect(() => {
    window.addEventListener("resize", handleResize)
  });

  const routeComponents = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => (
        route.protectedPageName ? (
          <Route
            path={route.path}
            key={route.path}
            element={
              <GuardedComponent
                exact={true}
                key={route.path}
                path={route.path}
                component={route.component}
                isAuthenticated={userIsAuthenticated}
                hasAccess={hasAccessToPage(route.protectedPageName)}
              />
            }
          />
        ) : (
          <Route
            key={route.path}
            path={route.path}
            element={<route.component/>}
          />
        )
      ))}
    </Routes>
  );

  // use in case the promise is empty string, to prevent errors.
  const routeComponentsWithoutStripe = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => (
        <Route
          key={route.path}
          path={route.path}
          element={<route.component/>}
        />
      ))}
    </Routes>
  );
  function handleCloseSnackbar() {
    dispatch(hideStatus());
  }

  const handleLogoutClick = async () => {
    await dispatch(logUserOut());
    window.location.reload();
  };

  const shouldShowNav = () => {
    return (
      userIsAuthenticated &&
      !window.location.pathname.includes("sign-document") &&
      !window.location.pathname.includes("sign-doc")
    ); // client signing URL
  };

  useEffect(() => {
    if (!hasCheckedUser.current && !(userIsAuthenticated && isSSO)) {
      dispatch(checkIfUserIsAuthenticated());
    }
    if (userIsAuthenticated && isSSO) {
        hasCheckedUser.current = true;
    }
  }, [dispatch, userIsAuthenticated, isSSO]);

  function toggleLoading(comp : JSX.Element){
    if (isSSOLoading) {
      return <LoadingOverlay whiteBackground={true}/>;
    }
    else{
      return comp;
    }
  }

  const isNotAuthenticated =(<UnauthenticatedAuthRouter/>);

  const isAuthenticated = (
    <>
      <AuthenticationMonitor />
      <div className="page-container">
        <FppHeader showLinks={shouldShowNav()} />
        {isLoading || isSSOLoading ? (
            <LoadingOverlay />
        ) : (
          <>
            {shouldShowNav() && (
              <Box sx={{ 
                width: '100%',
                paddingBottom: '4px',
                overflow: 'visible',
                position: 'relative'
              }}>
                <Box sx={{ 
                  width: '100%',
                  backgroundColor: 'white',
                  borderBottom: '3px solid rgba(0, 0, 0, 0.15)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                  position: 'relative',
                }}>
                  <SubNavigation
                    subNav={subNav}
                    subNavItemsToIgnore={subNavItemsToIgnore}
                    onNavClick={() => {
                      dispatch(clearPatient());
                      dispatch(clearEncounter());
                      dispatch(clearVOB());
                      dispatch(clearEstimate());
                      dispatch(clearFacilityPayers());
                      dispatch(clearRiskAssessment());
                      // Adding v3 clears to sub-nav
                      dispatch(clearSelectedVob());
                      dispatch(clearPatientVobList());
                      dispatch(clearSelectedEstimate());
                      dispatch(clearSavedEstimatesList());
                    }}
                  />
                  {shouldShowHubButton && <PaymentHubButton />}
                </Box>
              </Box>
            )}
            { /* prevents stripe initialization errors caused by empty string passed into 'stripe' prop */}
            {config.stripe_key ? (routeComponents) : (routeComponentsWithoutStripe)}
            {isAccountHolderUser && <div><FppFooter isMobile={isMobile} /></div>}
          </>
        )}
      </div>
    </>
  );

  return (
    <main>
    <div
      className={ channelType === 'mobile' ? "page-container mobile-page-container" : "page-container"}
      style={ channelType === 'mobile' || pathName==='payment-program-enrollment' ? { backgroundColor: "#FFF", height: "initial" } : { backgroundColor: "#F5F7F9" }}
    >
      <Router>
        <Grid container>
          {!userIsAuthenticated && (
            <>
            <Grid item xs={12}>
              <FppHeader showLinks={shouldShowNav()} />
            </Grid>
            </>
          )}
          {isPublicMode && !userIsAuthenticated && !isPublicModeLoggedIn && !isChannel && pathName === 'webpayment' && (
            <Grid container direction="row" style={{ backgroundColor: "#F5F7F9"}}>
              <Grid item direction="column" md={12} xs={12} style={{textAlign: "center", padding: "2em"}}>
                { (guestContext?.isExistingPatient === undefined) && (
                  <h1>
                    {!guestContext?.bannerText && (<CircularProgress />)}
                    {guestContext.bannerText}
                  </h1>
                )}
                { (guestContext?.isExistingPatient === true) && (
                  <div>
                    <h1>{existingPatientLoginMessage_1}</h1>
                    <h1>{existingPatientLoginMessage_2}</h1>
                    <h1>{existingPatientLoginMessage_3}</h1>
                  </div>
                )}
              </Grid>
            </Grid>
          )}

          {!isPublicModeLoggedIn && (guestContext?.isExistingPatient !== false) && pathName !== 'forgot-password' && pathName !== 'payment-program-enrollment' && (
          <Grid item md={isPublicMode && !userIsAuthenticated && (guestContext?.isExistingPatient === undefined) ? 5 : 12} xs={12}>
            {toggleLoading(userIsAuthenticated ? isAuthenticated : isNotAuthenticated)}
              <Snackbar
                open={applicationStatus.showMessage}
                message={applicationStatus.message}
                type={applicationStatus.messageType}
                onClose={handleCloseSnackbar}
              />
              <SessionTimeout
                isAuthenticated={userIsAuthenticated}
                logOut={handleLogoutClick}
              />
          </Grid>
          )}

          {/*  OR box  */}
          {isPublicMode && !userIsAuthenticated  && !isPublicModeLoggedIn && guestContext?.bannerText && !isChannel && (guestContext?.isExistingPatient === undefined) && pathName === 'webpayment' && (
            <Grid
              item
              md={2}
              xs={12}
              direction="column"
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "rgb(245,247,249)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  width: "80px",
                  height: "80px",
                  border: "3px solid #000",
                  marginTop: isMobile ? "20px" : "200px",
                  marginBottom: isMobile ? "20px" : "300px",
                }}
              >
                OR
              </div>
              <FinDigitalPlatformFooter showEmailFooter={false} channelType={channelType} />
            </Grid>
          )}

          {isPublicMode && !userIsAuthenticated  && (guestContext?.isExistingPatient !== true) && !showLogin && (
                <Grid item md={!isPublicModeLoggedIn ? 5 : 12} xs={12} style={ channelType === 'mobile' ? { backgroundColor: "#FFF" } : { backgroundColor: "#F5F7F9" }}>
                  {!isPublicModeLoggedIn  && guestContext?.bannerText && !isChannel &&(
                      <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" className={'pt-5'}>
                        <Grid item direction="column" md={12} xs={12} style={{ paddingLeft: "3%"}}>
                          Not already setup in the FinPay Patient Portal? Continue below.
                        </Grid>
                      </Grid>
                  )}
                  <div style={ channelType === 'mobile' || pathName==='payment-program-enrollment'? { backgroundColor: "#FFF" } : { backgroundColor: "#F5F7F9" }}>
                    <PublicPagesWrapper />
                  </div>
                </Grid>
          )}

        </Grid>
      </Router>
    </div>
    </main>
  );
}
export default App;
